import { useCallback, useMemo, useState } from "react";

export interface UseCartSelectionOptions {
  /**
   * All available cart IDs to select from
   */
  allCartIds: string[];
  /**
   * Whether to select all carts by default on mount
   * @default true
   */
  defaultSelectAll?: boolean;
}

export interface UseCartSelectionReturn {
  /**
   * Array of currently selected cart IDs
   */
  selectedCartIds: string[];
  /**
   * Whether at least one cart is selected (not all deselected)
   */
  hasSelection: boolean;
  /**
   * Toggle selection of a specific cart
   */
  toggleCart: (cartId: string) => void;
  /**
   * Select all available carts
   */
  selectAll: () => void;
  /**
   * Deselect all carts
   */
  clearAll: () => void;
  /**
   * Check if a specific cart is selected
   */
  isSelected: (cartId: string) => boolean;
  /**
   * Whether all available carts are selected
   */
  isAllSelected: boolean;
}

/**
 * Hook for managing multi-select cart filtering state.
 *
 * Features:
 * - Toggle individual cart selection
 * - Select all / Clear all operations
 * - Ids added to or removed from `allCartIds` are picked up in the same
 *   render, with no reconciliation pass and no frame of stale selection
 * - Default selection (`defaultSelectAll`) applies to any id the buyer
 *   hasn't explicitly chosen for, including ones that appear later
 *
 * @example
 * ```tsx
 * const {
 *   selectedCartIds,
 *   hasSelection,
 *   toggleCart,
 *   selectAll,
 *   clearAll
 * } = useCartSelection({
 *   allCartIds: carts.map(c => c.cart_id),
 *   defaultSelectAll: true
 * });
 * ```
 */
export function useCartSelection({
  allCartIds,
  defaultSelectAll = true
}: UseCartSelectionOptions): UseCartSelectionReturn {
  // Only the buyer's EXPLICIT per-id choices are stored. Anything absent falls
  // back to `defaultSelectAll`, so an id appearing for the first time is
  // classified during the very render it appears in.
  //
  // This deliberately replaces the older shape - store the selected ids, then
  // reconcile them against `allCartIds` in an effect. Effects run after
  // render, which left exactly one render where a newly-created cart wasn't
  // selected yet and a just-deleted one still was. Downstream that briefly
  // emptied the filtered cart list, and an empty list makes `CartPage`
  // return its whole-page "no matching filters" view - unmounting the page
  // and remounting it a frame later, which threw away both the page's scroll
  // position and the comparison grid's. Switching a product to a supplier you
  // had no cart with (creating one, and often emptying the old one) hit this
  // every time.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(
    () => new Map()
  );

  const selectedCartIds = useMemo(
    () => allCartIds.filter((id) => overrides.get(id) ?? defaultSelectAll),
    [allCartIds, overrides, defaultSelectAll]
  );

  const toggleCart = useCallback(
    (cartId: string) => {
      setOverrides((prev) => {
        const next = new Map(prev);
        next.set(cartId, !(prev.get(cartId) ?? defaultSelectAll));
        return next;
      });
    },
    [defaultSelectAll]
  );

  const selectAll = useCallback(() => {
    setOverrides(new Map(allCartIds.map((id) => [id, true])));
  }, [allCartIds]);

  const clearAll = useCallback(() => {
    setOverrides(new Map(allCartIds.map((id) => [id, false])));
  }, [allCartIds]);

  const isSelected = useCallback(
    (cartId: string) => overrides.get(cartId) ?? defaultSelectAll,
    [overrides, defaultSelectAll]
  );

  const hasSelection = selectedCartIds.length > 0;
  const isAllSelected =
    allCartIds.length > 0 && selectedCartIds.length === allCartIds.length;

  return {
    selectedCartIds,
    hasSelection,
    toggleCart,
    selectAll,
    clearAll,
    isSelected,
    isAllSelected
  };
}
